/* Liquid storefront adapter — behavioural capture + cross-domain handoff.
 *
 * The Web Pixel reliably reaches our backend on ALL devices (incl. iOS) but can
 * only see Shopify's standard commerce events. This adapter captures the extra
 * behaviour SILO tracks — element CLICKS (nav/CTA), SCROLL depth, time on page —
 * and hands them to the pixel via Shopify's custom-event channel
 * (Shopify.analytics.publish → analytics.subscribe('cja_behavior')). So clicks &
 * scroll flow on iOS too, keyed by the same clientId → one unified journey.
 *
 * Also decorates cross-domain links with a handoff token. Idempotent; bails in
 * the theme editor; never throws; no polling, no fetch/XHR patching.
 */
(function () {
  'use strict';
  if (window.__CJA_LIQUID__) return;
  window.__CJA_LIQUID__ = true;
  try { if (window.Shopify && Shopify.designMode) return; } catch (e) {}

  // Publish a behavioural event to the Web Pixel (reliable on all devices).
  function publish(name, props) {
    try {
      if (window.Shopify && Shopify.analytics && Shopify.analytics.publish) {
        Shopify.analytics.publish('cja_behavior', { name: name, props: props || {}, path: location.pathname });
      }
    } catch (e) {}
  }

  // ---- element attribution helpers ----
  function elText(el) { try { return (el.getAttribute('aria-label') || el.textContent || el.value || '').trim().replace(/\s+/g, ' ').slice(0, 80); } catch (e) { return ''; } }
  function labelFor(el) {
    try {
      var own = el.closest('label'); if (own) return own;
      if (!el.id) return null;
      if (!(window.CSS && CSS.escape)) return null;
      var id = CSS.escape(el.id);
      return document.querySelector('label[for="' + id + '"]');
    } catch (e) { return null; }
  }
  function elSelector(el) {
    try {
      var tag = el.tagName.toLowerCase();
      var id = el.id ? '#' + el.id : '';
      var cls = typeof el.className === 'string' && el.className.trim() ? '.' + el.className.trim().split(/\s+/).slice(0, 2).join('.') : '';
      return (tag + id + cls).slice(0, 120);
    } catch (e) { return ''; }
  }
  function elArea(el) {
    try {
      var a = el.closest('[data-cja-area],nav,header,footer,main,aside,[role="navigation"],[role="banner"],[role="contentinfo"]');
      return a ? (a.getAttribute('data-cja-area') || a.tagName.toLowerCase()).slice(0, 40) : 'body';
    } catch (e) { return 'body'; }
  }
  // Stable, deploy-proof element id: an author-set data-cja-id on the element or
  // its nearest labelled ancestor. Preferred over the CSS selector for grouping.
  function cjaIdOf(el) {
    try { var n = el.closest('[data-cja-id]'); return n ? String(n.getAttribute('data-cja-id')).slice(0, 80) : undefined; } catch (e) { return undefined; }
  }
  // Is this click part of the product-discovery UI (facet filter / sort / search /
  // pagination)? Detected from the destination URL and the surrounding widget so
  // the backend can measure "do filters help people find products?".
  function inFilterUI(el) {
    try { return !!el.closest('[data-cja-area="filter"],facet-filters-form,#FacetFiltersForm,collection-filters,#CollectionFiltersForm,.facets,.facets__form,.mobile-facets,.filter-group,.collection-filters,[data-filter],[data-facet],[data-param]'); } catch (e) { return false; }
  }
  function discoveryKind(el, hrefFull) {
    try {
      var h = hrefFull || '';
      if (/[?&](filter\.|constraint|pf_v_|pf_t_|pf_opt_)/.test(h)) return 'filter';
      if (/[?&]sort_by=/.test(h)) return 'sort';
      if (/[?&]page=\d/.test(h)) return 'pagination';
      if (/\/search\b/.test(h) || /[?&]q=/.test(h)) return 'search';
      if (inFilterUI(el)) return 'filter';
    } catch (e) {}
    return null;
  }

  // ---- clicks: nav_interaction (links) / cta_click (buttons) + frustration ----
  var rageTimes = [], rageKey = '';
  // Controls people legitimately click several times in a row (quantity steppers,
  // gallery arrows, carousel nav). Repeat clicks here are use, not frustration.
  var RAGE_EXEMPT = '.quantity__button,[name="plus"],[name="minus"],.cart-add,.cart-minus,.pcg-arrow,.swiper-button-next,.swiper-button-prev,[data-cja-repeat]';
  document.addEventListener('click', function (ev) {
    try {
      var t = ev.target; if (!t || !t.closest) return;
      var link = t.closest('a[href]');
      var cta = t.closest('[data-cja-cta],button,[role="button"],input[type="submit"],input[type="button"]');
      if (link) {
        var href = link.getAttribute('href') || ''; var toPath = ''; var external = false;
        try { var u = new URL(link.href, location.href); toPath = u.pathname; external = u.host !== location.host; } catch (e) {}
        var kind = discoveryKind(link, href);
        publish('nav_interaction', { label: elText(link), cjaId: cjaIdOf(link), selector: elSelector(link), area: elArea(link), href: href.slice(0, 300), toPath: toPath, external: external, kind: kind || (/מדריך מידות|size ?guide/i.test(elText(link)) ? 'size_guide' : undefined) });
      } else if (cta) {
        var ckind = inFilterUI(cta) ? 'filter' : (/מדריך מידות|size ?guide/i.test(elText(cta)) ? 'size_guide' : undefined);
        publish('cta_click', { label: cta.getAttribute('data-cja-cta') || elText(cta), cjaId: cjaIdOf(cta), selector: elSelector(cta), area: elArea(cta), kind: ckind });
      }
      // RAGE: 3+ clicks within 800ms → frustration.
      var now = Date.now(), key = elSelector(t);
      if (key !== rageKey) { rageKey = key; rageTimes = []; }
      var exempt = false; try { exempt = !!t.closest(RAGE_EXEMPT); } catch (e) {}
      if (exempt) { rageTimes = []; }
      else { rageTimes.push(now); rageTimes = rageTimes.filter(function (x) { return now - x < 800; }); }
      if (rageTimes.length >= 4) { publish('rage_click', { selector: elSelector(t), label: elText(t), area: elArea(t), cjaId: cjaIdOf(t) }); rageTimes = []; }
      // DEAD: clicked something that LOOKS clickable (cursor:pointer) but is wired to nothing.
      if (!link && !cta) {
        var interactive = t.closest('a,button,[role="button"],input,select,textarea,label,summary,[onclick],[data-cja-cta],[tabindex]');
        var pointer = false; try { pointer = getComputedStyle(t).cursor === 'pointer'; } catch (e) {}
        if (!interactive && pointer) publish('dead_click', { selector: elSelector(t), label: elText(t), area: elArea(t) });
      }
    } catch (e) {}
  }, true);

  // ---- filter / sort controls ----
  // On this theme discovery is driven by form controls, not links: ~68 filter
  // checkboxes inside <collection-filters> and a sort <select>. Clicks on those
  // are neither links nor buttons, so the click handler never saw them and filter
  // usage was invisible. Listen for `change` and emit the same cta_click shape.
  document.addEventListener('change', function (ev) {
    try {
      var el = ev.target;
      if (!el || !el.closest || !/^(input|select)$/i.test(el.tagName)) return;
      var kind = (el.name === 'sort_by' || el.closest('.sorting,[data-sort]')) ? 'sort'
               : (inFilterUI(el) ? 'filter' : null);
      if (!kind) return;
      var labelEl = labelFor(el) || el;
      var isToggle = el.type === 'checkbox' || el.type === 'radio';
      publish('cta_click', {
        label: elText(labelEl),
        cjaId: cjaIdOf(el),
        selector: elSelector(el),
        area: elArea(el),
        kind: kind,
        param: (el.dataset && (el.dataset.param || el.name)) || undefined,
        value: isToggle ? (el.checked ? 'on' : 'off') : String(el.value || '').slice(0, 80)
      });
    } catch (e) {}
  }, true);

  // ---- scroll depth via IntersectionObserver (NO scroll listener) ----
  // Invisible 1px markers are parked at 25/50/75/100% of the document; the
  // observer fires once as each crosses the viewport — off the main thread, no
  // per-scroll work. A ResizeObserver repositions them when the page grows.
  // Everything disconnects on pagehide, so nothing is retained.
  var maxScroll = 0, startTs = Date.now();
  (function setupScrollDepth() {
    if (!('IntersectionObserver' in window) || !document.body) return;
    var STEPS = [25, 50, 75, 100], markers = {}, io, ro;
    var host = document.createElement('div');
    host.setAttribute('aria-hidden', 'true');
    host.style.cssText = 'position:absolute;top:0;left:0;width:0;height:0;margin:0;padding:0;border:0;pointer-events:none;';
    STEPS.forEach(function (m) {
      var el = document.createElement('div');
      el.style.cssText = 'position:absolute;left:0;width:1px;height:1px;visibility:hidden;pointer-events:none;';
      el.setAttribute('data-cja-depth', m);
      host.appendChild(el); markers[m] = el;
    });
    document.body.appendChild(host);
    function place() {
      var docH = Math.max(document.documentElement.scrollHeight, document.body.scrollHeight);
      STEPS.forEach(function (m) { markers[m].style.top = Math.max(0, Math.round(docH * m / 100) - 1) + 'px'; });
    }
    place();
    io = new IntersectionObserver(function (entries) {
      for (var i = 0; i < entries.length; i++) {
        if (!entries[i].isIntersecting) continue;
        var m = +entries[i].target.getAttribute('data-cja-depth');
        if (m > maxScroll) { maxScroll = m; publish('scroll_depth', { depth: m }); }
        io.unobserve(entries[i].target); // each milestone fires at most once
      }
    }, { threshold: 0 });
    STEPS.forEach(function (m) { io.observe(markers[m]); });
    if (window.ResizeObserver) { ro = new ResizeObserver(place); ro.observe(document.body); }
    window.addEventListener('pagehide', function () { try { io.disconnect(); ro && ro.disconnect(); host.remove(); } catch (e) {} }, { once: true });
  })();
  window.addEventListener('pagehide', function () { try { publish('time_on_page', { ms: Date.now() - startTs, maxScroll: maxScroll }); } catch (e) {} }, { once: true });

  // ---- Core Web Vitals (PerformanceObserver — one report per page) ----
  // LCP/INP/CLS/FCP/TTFB: is the page fast? Reported once when the tab hides.
  (function setupWebVitals() {
    if (!('PerformanceObserver' in window)) return;
    var v = {}, reported = false;
    var observers = []; // kept so they can be DISCONNECTED once the page has reported
    function obs(type, cb, opts) { try { var po = new PerformanceObserver(cb); po.observe(Object.assign({ type: type, buffered: true }, opts || {})); observers.push(po); } catch (e) {} }
    obs('largest-contentful-paint', function (l) { var es = l.getEntries(); var last = es[es.length - 1]; if (last) v.lcp = Math.round(last.renderTime || last.loadTime || last.startTime); });
    var cls = 0; obs('layout-shift', function (l) { l.getEntries().forEach(function (e) { if (!e.hadRecentInput) cls += e.value; }); v.cls = Math.round(cls * 1000) / 1000; });
    var inp = 0; obs('event', function (l) { l.getEntries().forEach(function (e) { if (e.duration > inp) inp = e.duration; }); v.inp = Math.round(inp); }, { durationThreshold: 40 });
    obs('paint', function (l) { l.getEntries().forEach(function (e) { if (e.name === 'first-contentful-paint') v.fcp = Math.round(e.startTime); }); });
    function report() {
      if (reported) return; reported = true;
      observers.forEach(function (po) { try { po.disconnect(); } catch (e) {} }); observers = []; // one report per page, then release
      try { var nav = performance.getEntriesByType('navigation')[0]; if (nav) { v.ttfb = Math.round(nav.responseStart); v.load = Math.round(nav.loadEventEnd || nav.duration); } } catch (e) {}
      try { v.vw = window.innerWidth; } catch (e) {}
      publish('web_vitals', v);
    }
    document.addEventListener('visibilitychange', function () { if (document.visibilityState === 'hidden') report(); });
    window.addEventListener('pagehide', report, { once: true });
  })();

  // ---- JS errors / unhandled rejections (capped + deduped per page) ----
  (function setupErrors() {
    var count = 0, seen = {};
    function emit(msg, extra) {
      msg = String(msg || '').slice(0, 200);
      if (!msg || seen[msg] || count >= 10) return; seen[msg] = 1; count++;
      publish('app_error', Object.assign({ message: msg }, extra || {}));
    }
    window.addEventListener('error', function (ev) { try { if (ev.message) emit(ev.message, { source: (ev.filename || '').slice(0, 200), line: ev.lineno, col: ev.colno, stack: ((ev.error && ev.error.stack) || '').slice(0, 500) }); } catch (e) {} }, true);
    // A rejection whose reason is a plain object used to stringify to
    // "[object Object]" — unattributable. Serialise the constructor + body so
    // the report says WHAT rejected (vendor bridge objects, fetch payloads…).
    window.addEventListener('unhandledrejection', function (ev) {
      try {
        var r = ev.reason, msg;
        if (r && r.message) msg = r.message;
        else if (r && typeof r === 'object') {
          var ctor = (r.constructor && r.constructor.name) || 'Object';
          var body = '';
          try { body = JSON.stringify(r) || ''; } catch (e2) {
            try { body = '{' + Object.keys(r).slice(0, 8).join(',') + '}'; } catch (e3) {}
          }
          msg = 'non-Error rejection (' + ctor + ') ' + body.slice(0, 150);
        } else {
          msg = (r === undefined || r === null) ? 'unhandledrejection (' + String(r) + ')' : String(r);
        }
        emit(msg, { kind: 'promise', stack: ((r && r.stack) || '').slice(0, 500) });
      } catch (e) {}
    });
  })();

  // ---- element impressions (was the tracked CTA actually SEEN? → CTR denom) ----
  (function setupImpressions() {
    if (!('IntersectionObserver' in window)) return;
    // Every observed element is tracked so detached nodes (re-rendered banners,
    // removed CTAs) are UNOBSERVED and released — an observer keeps a strong
    // reference to its targets, which is how impressions could leak.
    var tracked = new Set();
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (!e.isIntersecting) return;
        var el = e.target; io.unobserve(el); tracked.delete(el); if (el.__cjaSeen) return; el.__cjaSeen = 1;
        // A promo surface without an author id gets a stable derived one ("promo:<class>").
        var pid = cjaIdOf(el);
        if (!pid) { try { var m = /(fomo|announcement|promo|bogo|countdown)[\w-]*/i.exec(el.className || ''); if (m) pid = 'promo:' + m[0].toLowerCase().slice(0, 40); } catch (e) {} }
        publish('element_impression', { cjaId: pid, selector: elSelector(el), label: elText(el), area: elArea(el) });
      });
    }, { threshold: 0.5 }); // ≥50% visible = actually seen
    var pending = null;
    // Tracked CTAs + PROMO surfaces (FOMO/announcement/BOGO/countdown banners), so
    // "was the promo seen?" becomes a measurable exposure (promo-exposure report).
    var PROMO_SEL = '.fomo-banner,[class*="fomo"],.announcement-bar,[class*="announcement"],[class*="promo"],[class*="bogo"],[class*="countdown"]';
    function scan() {
      try {
        tracked.forEach(function (el) { if (!el.isConnected) { try { io.unobserve(el); } catch (e) {} tracked.delete(el); } }); // release detached
        document.querySelectorAll('[data-cja-id],[data-cja-cta],' + PROMO_SEL).forEach(function (el) { if (!el.__cjaSeen && !tracked.has(el)) { io.observe(el); tracked.add(el); } });
      } catch (e) {}
    }
    scan();
    if (window.MutationObserver) { // pick up async-rendered CTAs, debounced
      var mo = new MutationObserver(function () { if (pending) return; pending = setTimeout(function () { pending = null; scan(); }, 1000); });
      try { mo.observe(document.body, { childList: true, subtree: true }); } catch (e) {}
    }
    window.addEventListener('pagehide', function () { try { io.disconnect(); mo && mo.disconnect(); tracked.clear(); } catch (e) {} }, { once: true });
  })();

  // ---- on-site search (Searchanise) — native Shopify search is bypassed, so the
  // Web Pixel's search_submitted never fires. We track it from the widget instead.
  function searchQuery() { try { return (new URLSearchParams(location.search).get('q') || '').slice(0, 120); } catch (e) { return ''; } }
  var lastResultsCount = null; // set once results render; stamped onto result clicks
  (function setupSearchTracking() {
    var onResults = /search-results-page/i.test(location.pathname) || !!searchQuery();
    if (!onResults) return;
    var q = searchQuery();
    // Refinement: did a previous (esp. zero-result) search lead here? One tiny
    // sessionStorage read — no listeners, no timers.
    var prev = null; try { prev = JSON.parse(sessionStorage.getItem('cja_last_search') || 'null'); } catch (e) {}
    var refined = !!(prev && prev.q && prev.q !== q && (Date.now() - (prev.at || 0)) < 10 * 60 * 1000);
    if (q) publish('search', { query: q, refinedFrom: refined ? prev.q : undefined, prevZero: refined ? !!prev.zero : undefined });
    // Results are client-rendered (skeleton → products). Watch for the render,
    // then read the count + zero-results once. Selectors are broad on purpose and
    // will be tightened once the live-widget probe confirms the exact classes.
    var fired = false, settle = null, mo = null;
    function readAndFire() {
      if (fired) return;
      var root = document.querySelector('[class*="snize-search-results"], .template-search__results, #MainContent, main') || document.body;
      if (root.querySelector('[class*="snize-skeleton"]')) return; // still loading
      var hrefs = {};
      root.querySelectorAll('a[href*="/products/"]').forEach(function (a) { try { hrefs[new URL(a.href, location.href).pathname] = 1; } catch (e) {} });
      var count = Object.keys(hrefs).length;
      var noResEl = root.querySelector('[class*="snize-no-result"],[class*="no-results"],[class*="no-match"],.template-search__no-results');
      fired = true;
      var engine = document.querySelector('[class*="snize-search-results"]') ? 'searchanise' : 'shopify';
      publish('search_results', { query: q, count: count, zeroResults: count === 0, hasNoResultsNode: !!noResEl, engine: engine });
      lastResultsCount = count;
      try { sessionStorage.setItem('cja_last_search', JSON.stringify({ q: q, zero: count === 0, at: Date.now() })); } catch (e) {}
      if (mo) { try { mo.disconnect(); } catch (e) {} }
    }
    if (window.MutationObserver) { mo = new MutationObserver(function () { if (settle) return; settle = setTimeout(function () { settle = null; readAndFire(); }, 600); }); try { mo.observe(document.body, { childList: true, subtree: true }); } catch (e) {} }
    setTimeout(readAndFire, 3000); // fallback if the render settled before we attached
    window.addEventListener('pagehide', function () { try { mo && mo.disconnect(); } catch (e) {} }, { once: true });
  })();
  // Delegated: search result clicks + Searchanise filters/sort (client-side widgets
  // the URL-based discoveryKind can't see).
  document.addEventListener('click', function (ev) {
    try {
      var t = ev.target; if (!t || !t.closest || !t.closest('[class*="snize"]')) return;
      var q = searchQuery();
      var plink = t.closest('[class*="snize"] a[href*="/products/"]');
      if (plink) {
        var pp = ''; try { pp = new URL(plink.href, location.href).pathname; } catch (e) {}
        // 1-based position among the distinct result links — ranking quality, not just CTR.
        // Computed only on a click (a handful of anchors), never on render.
        var pos; try { var seen = {}, i = 0, links = document.querySelectorAll('[class*="snize"] a[href*="/products/"]'); for (var k = 0; k < links.length; k++) { var hp = new URL(links[k].href, location.href).pathname; if (!seen[hp]) seen[hp] = ++i; } pos = seen[pp]; } catch (e) {}
        publish('search_result_click', { query: q, toPath: pp, label: elText(plink), position: pos, resultsCount: lastResultsCount == null ? undefined : lastResultsCount });
        return;
      }
      var sortEl = t.closest('[class*="snize-sort"],[class*="sort"]');
      var filterEl = t.closest('[class*="snize-filter"],[class*="snize-facet"],[class*="filter"],[class*="facet"]');
      if (sortEl) publish('sort_apply', { label: elText(sortEl), engine: 'searchanise' });
      else if (filterEl) publish('filter_apply', { label: elText(filterEl), engine: 'searchanise' });
    } catch (e) {}
  }, true);

  // ---- product page: friction + above-the-fold / stock state (only on /products/) ----
  // Explains the view→cart drop: did they pick a size? was the CTA/price even
  // visible on load? was it sold out? PERFORMANCE: delegated + passive listeners
  // that fire only on user action; ONE idle-scheduled state read after load (a few
  // getBoundingClientRect calls, off the LCP/INP path); nothing polls.
  (function setupProductPage() {
    if (!/^\/products\//.test(location.pathname)) return;
    function optionName(inp) {
      try {
        var m = /options\[(.+)\]/.exec(inp.name || ''); if (m) return m[1];
        // This theme names the inputs after the option itself ("מידה") — use that
        // before the legend, whose text also carries the size-guide link + an
        // inline SVG stylesheet (that junk showed up as the option name).
        if (inp.name && !/^(quantity|id)$/i.test(inp.name)) return String(inp.name).replace(/\s+/g, ' ').trim().replace(/-\d+$/, '');
        var f = inp.closest('fieldset,.product-form__input'); var lg = f && f.querySelector('legend,.form__label');
        if (!lg) return '';
        var txt = ''; for (var i = 0; i < lg.childNodes.length; i++) { var n = lg.childNodes[i]; if (n.nodeType === 3) txt += n.textContent; } // text nodes only
        return (txt.trim() || elText(lg)).replace(/[:\s]+$/, '').slice(0, 40);
      } catch (e) { return ''; }
    }
    function optKind(name) { name = String(name || '').toLowerCase(); if (/מיד[הו]|size|גודל|גדלים/.test(name)) return 'size_select'; if (/צבע|color|colour/.test(name)) return 'color_select'; return 'variant_select'; }
    document.addEventListener('change', function (ev) {
      try {
        var t = ev.target; if (!t || !t.closest) return;
        if (t.closest('quantity-input,.quantity') || t.name === 'quantity') { publish('cart_quantity_change', { where: 'pdp', qty: Number(t.value) || undefined }); return; }
        if (!(t.closest('variant-selects,variant-radios,.product-form__input') || /^options\[/.test(t.name || ''))) return;
        var name = optionName(t);
        var opt = t.tagName === 'SELECT' ? t.options[t.selectedIndex] : t;
        var lbl = t.tagName === 'SELECT' ? null : labelFor(t);
        var val = t.tagName === 'SELECT' ? (opt && opt.text) : (t.value || (lbl && elText(lbl)));
        var soldOut = !!((opt && (opt.disabled || /disabled|sold-?out/i.test(opt.className || ''))) || (lbl && /disabled|sold-?out/i.test(lbl.className || '')));
        publish(optKind(name), { option: String(name).slice(0, 40), value: String(val || '').slice(0, 60), soldOut: soldOut });
      } catch (e) {}
    }, true);
    document.addEventListener('click', function (ev) {
      try {
        var t = ev.target; if (!t || !t.closest) return;
        if (t.closest('.product-media-modal__toggle,[class*="zoom"],.product-media-modal')) publish('product_image_interaction', { kind: 'zoom' });
        else if (t.closest('media-gallery [class*="thumbnail"],.product__media-list li,.product-media-container,[class*="thumbnail"]')) publish('product_image_interaction', { kind: 'thumbnail' });
      } catch (e) {}
    }, true);
    var tx = null;
    document.addEventListener('touchstart', function (ev) { try { var t = ev.target; if (t.closest && t.closest('media-gallery,.product__media-wrapper,.product-media-container,[class*="swiper"]')) tx = ev.touches[0].clientX; } catch (e) {} }, { passive: true });
    document.addEventListener('touchend', function (ev) { try { if (tx == null) return; var dx = ev.changedTouches[0].clientX - tx; tx = null; if (Math.abs(dx) > 30) publish('product_image_interaction', { kind: 'swipe' }); } catch (e) {} }, { passive: true });
    var idle = window.requestIdleCallback || function (cb) { return setTimeout(cb, 1500); };
    function state() {
      try {
        var vh = window.innerHeight || 0;
        var inView = function (el) { if (!el) return null; var r = el.getBoundingClientRect(); return r.top < vh && r.bottom > 0; };
        var cta = document.querySelector('.product-form__submit,form[action*="/cart/add"] [type="submit"]');
        var price = document.querySelector('.price__container,.product__price,[class*="price__"]');
        var txt = cta ? elText(cta) : '';
        var opts = document.querySelectorAll('variant-selects input[type="radio"],variant-selects select option,.product-form__input input[type="radio"]');
        var so = 0; for (var i = 0; i < opts.length; i++) { var o = opts[i]; var l = labelFor(o); if (o.disabled || /disabled|sold-?out/i.test((o.className || '') + ' ' + (l ? l.className : ''))) so++; }
        var p; try { var m = window.ShopifyAnalytics && ShopifyAnalytics.meta && ShopifyAnalytics.meta.product; p = m && m.variants && m.variants[0] && (m.variants[0].price / 100); } catch (e) {}
        publish('product_page_state', { ctaInView: inView(cta), priceInView: inView(price), soldOut: !!(cta && (cta.disabled || /אזל|sold ?out/i.test(txt))), soldOutOptions: so, totalOptions: opts.length, price: p });
      } catch (e) {}
    }
    if (document.readyState === 'complete') idle(state); else window.addEventListener('load', function () { idle(state); }, { once: true });
  })();

  // ---- session RECORDER (PostHog), server-controlled ----
  // Loads the recorder ONLY after load + idle, only when the backend's
  // recording-config says it's on, and only for the sampled share of sessions
  // (a kill switch and a cost dial with no theme deploy). Minimal init: recording
  // only — our own SDK does the analytics. The config is cached 24h, so this is
  // one small fetch per visitor per day; the recorder script itself is async and
  // off the critical path. If PostHog is already on the page, nothing happens.
  (function loadRecorder() {
    if (window.posthog) return;
    var base = ((window.__CJA_CONFIG__ && window.__CJA_CONFIG__.ingestUrl) || '').replace(/\/$/, '');
    if (!base || !window.fetch) return;
    function init(c) {
      try {
        if (!c || !c.enabled || !c.apiKey || window.posthog) return;
        var on = sessionStorage.getItem('cja_rec');
        if (on == null) { on = Math.random() < (c.sampleRate == null ? 1 : c.sampleRate) ? '1' : '0'; sessionStorage.setItem('cja_rec', on); }
        if (on !== '1') return;
        var host = (c.host || 'https://us.i.posthog.com').replace(/\/$/, '');
        var s = document.createElement('script'); s.async = true; s.src = host + '/static/array.js';
        s.onload = function () {
          try {
            window.posthog.init(c.apiKey, {
              api_host: host, ui_host: c.uiHost || undefined,
              capture_pageview: false, capture_pageleave: false, autocapture: false, // analytics is ours
              disable_session_recording: false,
              session_recording: { maskAllInputs: c.maskInputs !== false },
              persistence: 'localStorage+cookie',
            });
          } catch (e) {}
        };
        document.head.appendChild(s);
      } catch (e) {}
    }
    var idle = window.requestIdleCallback || function (cb) { return setTimeout(cb, 1500); };
    function go() {
      idle(function () {
        try {
          var cached = null; try { cached = JSON.parse(localStorage.getItem('cja_rec_cfg') || 'null'); } catch (e) {}
          if (cached && Date.now() - (cached.at || 0) < 86400000) return init(cached.c);
          fetch(base + '/recording-config', { credentials: 'omit' })
            .then(function (r) { return r.ok ? r.json() : null; })
            .then(function (c) { try { localStorage.setItem('cja_rec_cfg', JSON.stringify({ at: Date.now(), c: c })); } catch (e) {} init(c); })
            .catch(function () {});
        } catch (e) {}
      });
    }
    if (document.readyState === 'complete') go(); else window.addEventListener('load', go, { once: true });
  })();

  // ---- session-replay linkage (PostHog, when it is on the page) ----
  // Hands our identity to PostHog and records its ids on our session, so a rage
  // click or an abandoned product page links to a watchable replay. A bounded
  // idle retry (PostHog may load late), then it stops — never a running timer.
  (function setupReplayLink() {
    function getCookie(n) { try { var m = document.cookie.match('(?:^|; )' + n.replace(/[.$?*|{}()[\]\\/+^]/g, '\\$&') + '=([^;]*)'); return m ? decodeURIComponent(m[1]) : null; } catch (e) { return null; } }
    var tries = 0, idle = window.requestIdleCallback || function (cb) { return setTimeout(cb, 500); };
    function attempt() {
      try {
        var ph = window.posthog;
        if (ph && ph.get_distinct_id) {
          try { ph.register({ cja_client_id: getCookie('_shopify_y') || undefined }); } catch (e) {}
          var did = ph.get_distinct_id(); var sid = ph.get_session_id ? ph.get_session_id() : undefined;
          if (did) publish('replay_link', { distinctId: String(did).slice(0, 120), replaySessionId: sid ? String(sid).slice(0, 120) : undefined, provider: 'posthog' });
          return;
        }
      } catch (e) {}
      if (++tries < 15) setTimeout(function () { idle(attempt); }, 1000); // bounded: the recorder loads after idle, so allow ~15s, then stop for good
    }
    idle(attempt);
  })();

  // ---- cross-domain handoff (needs the SDK's token; degrade gracefully) ----
  var HOSTS = (window.__CJA_CONFIG__ && window.__CJA_CONFIG__.hydrogenHosts) || [];
  if (HOSTS.length) {
    document.addEventListener('click', function (ev) {
      try {
        if (ev.button !== 0 || ev.metaKey || ev.ctrlKey || ev.shiftKey || ev.altKey || ev.defaultPrevented) return;
        var a = ev.target && ev.target.closest ? ev.target.closest('a[href]') : null;
        if (!a || a.__cja || (a.target && a.target !== '_self') || a.hasAttribute('download')) return;
        var host = new URL(a.href).hostname;
        if (host === location.hostname || HOSTS.indexOf(host) === -1) return;
        if (!window.CJA || !window.CJA.handoff) return;
        var href = a.href; ev.preventDefault();
        var done = false;
        var go = function (u) { if (done) return; done = true; a.__cja = true; location.href = u; };
        setTimeout(function () { go(href); }, 1200);
        Promise.resolve(window.CJA.handoff(href)).then(go, function () { go(href); });
      } catch (e) {}
    }, true);
  }
})();
